import { createContext, useContext, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Check, ArrowRight, Phone, Mail, Loader2 } from "lucide-react";
import { ELIGIBILITY_STEPS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EligibilityContext = createContext(null);
export const useEligibility = () => useContext(EligibilityContext);

export const EligibilityProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  return (
    <EligibilityContext.Provider value={{ open: openModal }}>
      {children}
      <InquiryModal open={open} onClose={() => setOpen(false)} />
    </EligibilityContext.Provider>
  );
};

const InquiryModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(0);
    setAnswers({});
    setName("");
    setPhone("");
    setEmail("");
    setSubmitting(false);
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const totalSteps = ELIGIBILITY_STEPS.length + 1;
  const progress = (step / totalSteps) * 100;

  const selectOption = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async () => {
    const cleanName = name.trim();
    if (cleanName.length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    const clean = phone.replace(/[^\d+]/g, "");
    if (clean.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    const cleanEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: cleanName,
        phone: clean,
        email: cleanEmail,
        experience: answers.experience,
        focus: answers.focus,
        timeline: answers.timeline,
        capital: answers.capital,
      });
      window.location.href = "/thank-you";
    } catch (e) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  const isPhoneStep = step === ELIGIBILITY_STEPS.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="inquiry-modal-overlay"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={close} />
          <motion.div
            className="relative z-10 w-full max-w-lg overflow-hidden rounded-t-3xl border border-surfaceline bg-ink2 shadow-2xl sm:rounded-3xl"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            data-testid="inquiry-modal"
          >
            <div className="h-1 w-full bg-surface">
              <motion.div
                className="h-full bg-gradient-to-r from-em1 to-em2"
                animate={{ width: `${progress}%` }}
                transition={{ ease: "easeOut" }}
              />
            </div>

            <button
              onClick={close}
              aria-label="Close"
              data-testid="inquiry-close-btn"
              className="absolute right-4 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-surfaceline bg-surface text-mutedgreen transition-colors hover:text-offwhite"
            >
              <X size={18} />
            </button>

            <div className="p-6 pt-8 sm:p-8">
              <>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.24em] text-gold">
                    Inquiry · {Math.min(step + 1, totalSteps)}/{totalSteps}
                  </p>

                  <AnimatePresence mode="wait">
                    {!isPhoneStep ? (
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -24 }}
                        transition={{ duration: 0.3 }}
                      >
                        <h3 className="font-display text-3xl font-bold uppercase leading-tight text-offwhite">
                          {ELIGIBILITY_STEPS[step].question}
                        </h3>
                        <p className="mt-2 text-sm text-mutedgreen">
                          {ELIGIBILITY_STEPS[step].caption}
                        </p>
                        <div className="mt-6 flex flex-col gap-3">
                          {ELIGIBILITY_STEPS[step].options.map((opt) => {
                            const active = answers[ELIGIBILITY_STEPS[step].key] === opt;
                            return (
                              <button
                                key={opt}
                                data-testid={`inquiry-option-${step}-${opt
                                  .toLowerCase()
                                  .replace(/[^a-z0-9]+/g, "-")
                                  .replace(/(^-|-$)/g, "")}`}
                                onClick={() => selectOption(ELIGIBILITY_STEPS[step].key, opt)}
                                className={`group flex min-h-[56px] items-center justify-between rounded-2xl border px-5 text-left text-base font-medium transition-colors ${
                                  active
                                    ? "border-em2 bg-em2/10 text-offwhite"
                                    : "border-surfaceline bg-surface text-offwhite/90 hover:border-em2/60"
                                }`}
                              >
                                {opt}
                                <span
                                  className={`flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
                                    active
                                      ? "border-em2 bg-em2 text-ink"
                                      : "border-surfaceline text-transparent group-hover:border-em2/60"
                                  }`}
                                >
                                  <Check size={14} />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="phone"
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -24 }}
                        transition={{ duration: 0.3 }}
                      >
                        <h3 className="font-display text-3xl font-bold uppercase leading-tight text-offwhite">
                          Where should we call you back?
                        </h3>
                        <p className="mt-2 text-sm text-mutedgreen">
                          Our Ahmedabad team will call to help you pick the right
                          batch. No spam, ever.
                        </p>
                        <div className="mt-6 flex flex-col gap-3">
                          <input
                            data-testid="inquiry-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Your name"
                            className="min-h-[56px] rounded-2xl border border-surfaceline bg-surface px-5 text-base text-offwhite placeholder:text-mutedgreen/60 focus:border-em2 focus:outline-none"
                          />
                          <div className="flex min-h-[56px] items-center rounded-2xl border border-surfaceline bg-surface px-5 focus-within:border-em2">
                            <Phone size={18} className="mr-3 text-em2" />
                            <span className="mr-2 text-base text-mutedgreen">+91</span>
                            <input
                              data-testid="inquiry-phone-input"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              inputMode="tel"
                              placeholder="Mobile number"
                              className="w-full bg-transparent text-base text-offwhite placeholder:text-mutedgreen/60 focus:outline-none"
                            />
                          </div>
                          <div className="flex min-h-[56px] items-center rounded-2xl border border-surfaceline bg-surface px-5 focus-within:border-em2">
                            <Mail size={18} className="mr-3 text-em2" />
                            <input
                              data-testid="inquiry-email-input"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              inputMode="email"
                              type="email"
                              placeholder="Email address"
                              className="w-full bg-transparent text-base text-offwhite placeholder:text-mutedgreen/60 focus:outline-none"
                            />
                          </div>
                          <button
                            data-testid="inquiry-submit-btn"
                            onClick={submit}
                            disabled={submitting}
                            className="btn-emerald shimmer-cta mt-1 flex min-h-[56px] items-center justify-center gap-2 rounded-2xl text-base font-bold text-white transition-transform hover:scale-[1.01] disabled:opacity-70"
                          >
                            {submitting ? (
                              <>
                                <Loader2 size={18} className="animate-spin" /> Submitting…
                              </>
                            ) : (
                              <>
                                Request Callback <ArrowRight size={18} />
                              </>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
              </>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
